/**
 * Content templates for the New screen (Publisher-style).
 *
 * Every template is a small builder that receives the chosen colour scheme (C),
 * font scheme (F) and the user's business information, and returns page specs.
 * Templates are re-built live while the user customises them, so the previews
 * in the New screen always show the real result.
 */
import { v4 as uuidv4 } from 'uuid';
import { SCHEME_PRESETS, tint, shade, hexToHsl, schemeVariants, ensureSchemeRegistered } from './colors';
import { escapeHtml, htmlToText } from './textUtils';
import { PAGE_PRESETS, DEFAULT_TEXT_PROPS } from '../context/EditorContext';

/* ------------------------------------------------------------------ */
/* Schemes                                                             */
/* ------------------------------------------------------------------ */

export const FONT_SCHEMES = {
  modern: { name: 'Modern', heading: 'Montserrat', body: 'Inter' },
  classic: { name: 'Classic', heading: 'Playfair Display', body: 'Lora' },
  editorial: { name: 'Editorial', heading: 'Merriweather', body: 'Open Sans' },
  friendly: { name: 'Friendly', heading: 'Poppins', body: 'Nunito' },
  bold: { name: 'Bold', heading: 'Bebas Neue', body: 'Roboto' },
  technical: { name: 'Technical', heading: 'Oswald', body: 'Work Sans' },
};

export const TEMPLATE_CATEGORIES = [
  { id: 'flyer', label: 'Flyers' },
  { id: 'newsletter', label: 'Newsletters' },
  { id: 'brochure', label: 'Brochures' },
  { id: 'menu', label: 'Menus' },
  { id: 'certificate', label: 'Certificates' },
  { id: 'card', label: 'Business Cards' },
  { id: 'postcard', label: 'Postcards' },
];

export const BUSINESS_DEFAULTS = {
  organization: 'Your Business Name',
  name: 'Your Name',
  title: 'Job Title',
  phone: '(555) 010-2000',
  email: 'hello@yourbusiness.com',
  website: 'www.yourbusiness.com',
  address: '123 Main Street\nSpringfield, ST 12345',
  tagline: 'Your tagline or motto goes here',
};

/** Readable text colour for a given background. */
export const readableOn = (hex, dark = '#111111') => (hexToHsl(hex).l > 62 ? dark : '#ffffff');

const palette = (scheme) => {
  const hex = (i) => scheme[i]?.hex || '#000000';
  const primary = hex(2);
  const secondary = hex(3);
  const dark = hex(1);
  const strong = hex(5);
  return {
    text: hex(0),
    dark,
    primary,
    secondary,
    strong,
    bg: hex(7),
    light: tint(primary, 0.86),
    soft: tint(secondary, 0.8),
    pale: tint(dark, 0.9),
    primaryDark: shade(primary, 0.3),
    onPrimary: readableOn(primary),
    onSecondary: readableOn(secondary),
    onDark: readableOn(dark),
    onStrong: readableOn(strong),
    // small text in the secondary colour must stay legible on white (e.g. Harvest's gold)
    secondaryText: hexToHsl(secondary).l > 50 ? shade(secondary, 0.4) : secondary,
  };
};

/* ------------------------------------------------------------------ */
/* Object helpers                                                      */
/* ------------------------------------------------------------------ */

const uid = () => uuidv4();
const esc = escapeHtml;
const div = (content, style) => `<div${style ? ` style="${style}"` : ''}>${content || '<br>'}</div>`;
const paras = (...lines) => lines.map((l) => div(l)).join('');

const textBox = (x, y, w, h, html, props = {}) => ({
  id: uid(), type: 'textbox', x, y, width: w, height: h, rotation: 0, locked: false, visible: true,
  properties: { ...DEFAULT_TEXT_PROPS, html, text: htmlToText(html), paddingTop: 4, paddingRight: 5, paddingBottom: 4, paddingLeft: 5, ...props },
});

const shape = (shapeType, x, y, w, h, { rotation = 0, ...props } = {}) => ({
  id: uid(), type: 'shape', shapeType, x, y, width: w, height: h, rotation, locked: false, visible: true,
  properties: { fill: { type: 'solid', color: '#000000' }, stroke: { color: '#000000', width: 0 }, cornerRadius: 0, opacity: 100, wrapStyle: 'none', ...props },
});

const rect = (x, y, w, h, color, props = {}) => shape('rectangle', x, y, w, h, { fill: { type: 'solid', color }, ...props });

const picture = (x, y, w, h, photoQuery, props = {}) => ({
  id: uid(), type: 'image', x, y, width: w, height: h, rotation: 0, locked: false, visible: true,
  properties: { src: '', placeholder: true, photoQuery, objectFit: 'cover', opacity: 100, borderRadius: 0, wrapStyle: 'square', wrapDistance: 12, altText: photoQuery, ...props },
});

const heading = (F, C, size, extra = {}) => ({ fontFamily: F.heading, fontSize: size, fontWeight: 'bold', color: C.dark, lineHeight: 1.08, ...extra });
const body = (F, C, size = 12.5, extra = {}) => ({ fontFamily: F.body, fontSize: size, color: C.text, lineHeight: 1.45, ...extra });
const label = (F, C, size = 10, extra = {}) => ({ fontFamily: F.body, fontSize: size, color: C.secondaryText, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 'bold', ...extra });

const bi = (info, key) => esc(((info && info[key]) || '').trim() || BUSINESS_DEFAULTS[key]);
const biLines = (info, key) => bi(info, key).split('\n').map((l) => l.trim()).filter(Boolean);
const addressInline = (info) => biLines(info, 'address').join(', ');

/* ------------------------------------------------------------------ */
/* Copy                                                                */
/* ------------------------------------------------------------------ */

const COPY = {
  eventBody: 'Join us for an unforgettable evening of live music under the stars. Three stages, twelve bands and food trucks from across the region, finishing with a fireworks display at 10:30 PM. Bring a blanket, bring your friends and bring your dancing shoes — this is the night the whole town has been waiting for.',
  noticeBody: [
    'Every spring our neighbourhood garden wakes up, and every spring we need extra hands to help it along. Whether you can spare an hour or the whole day, there is a job for you: turning beds, planting seedlings, repairing the raised boxes and painting the tool shed.',
    'No experience is necessary. Our volunteer leads will show you what to do, and we will have gloves, tools, sunscreen and plenty of lemonade on hand. Children are welcome when accompanied by an adult, and there will be a seed-planting table just for them.',
    'The garden provides fresh produce to more than sixty families in our community each season. A morning of your time makes a real difference — and you will leave with a plant of your own to take home.',
  ],
  saleIntro: 'Our biggest event of the year is here. For one weekend only, enjoy storewide savings on the styles you love — plus free gift wrapping on every purchase.',
  newsLead: [
    'This month marks an important milestone for our organisation. After a year of planning, our new community programme launched to a full house, welcoming more than two hundred neighbours through the doors on opening night.',
    'The programme brings together local volunteers, partner organisations and small businesses to offer weekly workshops, a mentoring scheme and a shared workspace open to everyone. "We wanted to build something that belongs to the whole community," said the programme director. "The response has exceeded every expectation."',
    'Over the coming months we will expand the timetable to include evening sessions and a monthly showcase where participants present their projects. Registration is open now and places are filling quickly, so we encourage members to sign up early.',
    'Thank you to everyone who made the launch possible — from the volunteers who stayed late to set up the hall to the sponsors who provided refreshments. This is only the beginning.',
  ],
  story1: 'Our volunteer team grew by forty percent this quarter, thanks in large part to the new referral scheme. Returning volunteers can now nominate a friend, and both receive priority booking for popular sessions. We celebrate every new face — welcome aboard!',
  story2: 'Preparations are under way for the annual fundraising dinner. This year the event moves to a larger venue with room for three hundred guests, live entertainment and a silent auction featuring donations from local businesses. Tickets go on sale next month.',
  story3: 'A new partnership with the public library brings a rotating collection of books and learning resources to our lobby. Members may borrow items for up to three weeks using their membership card. The first collection focuses on small-business skills.',
  quote: '“Alone we can do so little; together we can do so much.”',
  postcardMsg: 'Dear Neighbour,\n\nWe are delighted to announce the grand opening of our new location on Main Street. Join us on Saturday for refreshments, live music and exclusive opening-day offers.\n\nWe look forward to welcoming you.',
};

/* ------------------------------------------------------------------ */
/* Templates                                                           */
/* ------------------------------------------------------------------ */

const flyerEvent = {
  id: 'flyer-event', category: 'flyer', name: 'Event Flyer', preset: 'letter', pageCount: 1,
  description: 'A bold header band with a large photo, event details bar, highlights box and a contact footer. Ideal for concerts, fairs and launches.',
  build: ({ C, F, info }, { W, H }) => {
    const m = 48;
    const cw = W - m * 2;
    return {
      pages: [{
        name: 'Flyer',
        objects: [
          rect(0, 0, W, 300, C.primary),
          textBox(m, 44, cw, 30, div('You are invited to'), label(F, C, 12, { color: readableOn(C.primary), letterSpacing: 4 })),
          textBox(m, 76, cw - 160, 120, div('Summer Music Festival'), heading(F, C, 60, { color: C.onPrimary, autofit: 'shrink' })),
          picture(m, 210, cw, 380, 'live concert stage lights crowd', { stroke: { color: '#ffffff', width: 6 } }),
          shape('circle', W - m - 140, 140, 140, 140, { fill: { type: 'solid', color: C.strong }, stroke: { color: '#ffffff', width: 4 } }),
          textBox(W - m - 140, 140, 140, 140, div('FREE') + div('entry', 'font-size: 12px; letter-spacing: 3px'), heading(F, C, 30, { color: C.onStrong, textAlign: 'center', verticalAlign: 'middle', lineHeight: 1.1 })),
          textBox(m, 608, cw, 56, div(`<b>Saturday, June 21</b> · 6:00 PM – 11:00 PM · Riverside Park Amphitheater`), body(F, C, 17, { textAlign: 'center', verticalAlign: 'middle', color: C.dark, background: { type: 'solid', color: C.light } })),
          textBox(m, 684, 430, 230, paras(COPY.eventBody, '', `<b>Tickets:</b> Free admission. Reserve your spot at ${bi(info, 'website')}.`), body(F, C, 13.5)),
          textBox(m + 460, 684, cw - 460, 230, div('Highlights', 'letter-spacing: 3px; font-size: 11px') + div('<br>') + paras('• Three live stages', '• Food trucks & craft market', '• Kids zone until 8 PM', '• Fireworks finale 10:30 PM'), body(F, C, 13, { color: C.onSecondary, background: { type: 'solid', color: C.secondary }, paddingLeft: 18, paddingTop: 16, paddingRight: 16, paddingBottom: 16, cornerRadius: 6, lineHeight: 1.7 })),
          rect(0, H - 96, W, 96, C.dark),
          textBox(m, H - 84, cw, 72, div(`<b>${bi(info, 'organization')}</b> · ${bi(info, 'phone')} · ${bi(info, 'website')}`) + div(addressInline(info), 'font-size: 12px'), body(F, C, 14, { color: C.onDark, textAlign: 'center', verticalAlign: 'middle' })),
        ],
      }],
    };
  },
};

const flyerSale = {
  id: 'flyer-sale', category: 'flyer', name: 'Sale Flyer', preset: 'letter', pageCount: 1,
  description: 'Full-width hero photo, colour band headline, a starburst discount badge and three offer columns above a contact strip.',
  build: ({ C, F, info }, { W, H }) => {
    const m = 48;
    const cw = W - m * 2;
    const colW = (cw - 40) / 3;
    const offers = [['New arrivals', '20% off the entire spring collection, including accessories.'], ['Clearance', 'Up to 50% off last season\u2019s favourites while stocks last.'], ['Members', 'Double loyalty points all weekend plus a free gift with purchase.']];
    return {
      pages: [{
        name: 'Flyer',
        objects: [
          picture(0, 0, W, 520, 'fashion boutique clothing rack'),
          rect(0, 470, W, 120, C.primary),
          textBox(m, 476, cw - 220, 108, div('Season Sale'), heading(F, C, 64, { color: C.onPrimary, verticalAlign: 'middle', autofit: 'shrink', textTransform: 'uppercase', letterSpacing: 2 })),
          shape('star-12', W - m - 200, 380, 210, 210, { fill: { type: 'solid', color: C.strong }, stroke: { color: '#ffffff', width: 5 } }),
          textBox(W - m - 200, 380, 210, 210, div('up to', 'font-size: 14px; letter-spacing: 3px') + div('50%') + div('off', 'font-size: 16px; letter-spacing: 3px'), heading(F, C, 52, { color: C.onStrong, textAlign: 'center', verticalAlign: 'middle', lineHeight: 1 })),
          textBox(m, 606, cw, 60, div(COPY.saleIntro), body(F, C, 14.5, { textAlign: 'center', color: C.dark, fontStyle: 'italic' })),
          ...offers.flatMap(([title, text], i) => {
            const x = m + i * (colW + 20);
            return [
              rect(x, 690, 56, 5, C.secondary),
              textBox(x, 704, colW, 44, div(title), heading(F, C, 22)),
              textBox(x, 748, colW, 120, div(text), body(F, C, 13)),
            ];
          }),
          rect(m, 890, cw, 1, C.secondary),
          textBox(m, 904, cw, 44, div(`This weekend only · Friday to Sunday · 9 AM – 8 PM`), label(F, C, 12, { textAlign: 'center', verticalAlign: 'middle', color: C.primary })),
          textBox(m, H - 100, cw, 60, div(`<b>${bi(info, 'organization')}</b> · ${addressInline(info)}`) + div(`${bi(info, 'phone')} · ${bi(info, 'website')}`), body(F, C, 13, { color: C.dark, textAlign: 'center', verticalAlign: 'middle', background: { type: 'solid', color: C.light }, cornerRadius: 6 })),
        ],
      }],
    };
  },
};

const flyerNotice = {
  id: 'flyer-notice', category: 'flyer', name: 'Community Notice', preset: 'letter', pageCount: 1,
  description: 'A calm side band with the date and contact details, a big headline and body copy that wraps around a photo. Great for volunteer days, meetings and announcements.',
  build: ({ C, F, info }, { W, H }) => {
    const side = 220;
    const x = side + 40;
    const cw = W - x - 48;
    return {
      pages: [{
        name: 'Flyer',
        objects: [
          rect(0, 0, side, H, C.dark),
          shape('circle', 60, 56, 100, 100, { fill: { type: 'solid', color: C.primary } }),
          textBox(60, 56, 100, 100, div(bi(info, 'organization').charAt(0).toUpperCase()), heading(F, C, 48, { color: C.onPrimary, textAlign: 'center', verticalAlign: 'middle' })),
          textBox(36, 176, side - 72, 90, div(bi(info, 'organization')), heading(F, C, 20, { color: C.onDark, autofit: 'shrink' })),
          textBox(36, 300, side - 72, 40, div('Save the date'), label(F, C, 10, { color: tint(C.primary, 0.5) })),
          textBox(36, 336, side - 72, 150, div('Sat', 'font-size: 18px; letter-spacing: 3px') + div('14') + div('September', 'font-size: 16px; letter-spacing: 2px'), heading(F, C, 72, { color: C.onDark, lineHeight: 1, textTransform: 'uppercase' })),
          textBox(36, 500, side - 72, 60, div('9:00 AM – 1:00 PM') + div('Elm Street Garden'), body(F, C, 12, { color: C.onDark, lineHeight: 1.6 })),
          rect(36, H - 190, side - 72, 1, tint(C.dark, 0.4)),
          textBox(36, H - 176, side - 72, 130, paras(bi(info, 'phone'), bi(info, 'email'), bi(info, 'website'), ...biLines(info, 'address')), body(F, C, 10.5, { color: C.onDark, lineHeight: 1.6 })),
          textBox(x, 60, cw, 140, div('Volunteer Day at the Community Garden'), heading(F, C, 42, { autofit: 'shrink' })),
          textBox(x, 206, cw, 44, div('Bring gloves, bring friends — we provide the rest.'), body(F, C, 18, { fontStyle: 'italic', color: C.primary })),
          rect(x, 258, 72, 4, C.primary),
          textBox(x, 278, cw, 500, paras(...COPY.noticeBody), body(F, C, 13.5, { lineHeight: 1.55, textAlign: 'justify' })),
          picture(x + cw - 250, 300, 250, 190, 'community garden volunteers planting', { wrapStyle: 'square', wrapDistance: 14 }),
          textBox(x, 800, cw, 120, div('What to bring', 'letter-spacing: 3px; font-size: 11px; font-weight: bold') + div('<br>') + div('Sturdy shoes · Sun hat · Refillable water bottle · Your favourite gardening tips'), body(F, C, 13, { color: C.dark, background: { type: 'solid', color: C.light }, paddingLeft: 18, paddingRight: 18, paddingTop: 14, paddingBottom: 14, cornerRadius: 6, verticalAlign: 'middle' })),
          rect(x, H - 110, cw, 2, C.secondary),
          textBox(x, H - 100, cw, 56, div(`Questions? Call ${bi(info, 'phone')} or visit ${bi(info, 'website')}`), body(F, C, 12, { color: C.secondaryText, textAlign: 'center', verticalAlign: 'middle' })),
        ],
      }],
    };
  },
};

const newsletterClassic = {
  id: 'newsletter-classic', category: 'newsletter', name: 'Classic Newsletter', preset: 'letter', pageCount: 2,
  description: 'Traditional masthead with rules, a two-column lead story wrapping around a photo, sidebar boxes and a second page of stories. Footer lives on the master page.',
  build: ({ C, F, info }, { W, H }) => {
    const m = 48;
    const cw = W - m * 2;
    const main = 480;
    const sideX = m + main + 24;
    const sideW = cw - main - 24;
    const master = [
      rect(m, H - 70, cw, 1, C.secondary),
      textBox(m, H - 64, cw, 30, div(`${bi(info, 'organization')} · ${bi(info, 'website')} · ${bi(info, 'phone')}`), body(F, C, 10, { color: C.secondaryText, textAlign: 'center' })),
    ];
    const page1 = [
      textBox(m, 36, cw, 92, div(bi(info, 'organization')), heading(F, C, 50, { textAlign: 'center', verticalAlign: 'middle', autofit: 'shrink' })),
      rect(m, 130, cw, 3, C.primary),
      rect(m, 136, cw, 1, C.dark),
      textBox(m, 140, cw, 26, div(`Volume 1, Issue 1 · This Month · ${bi(info, 'tagline')}`), label(F, C, 9.5, { textAlign: 'center', letterSpacing: 1.5 })),
      textBox(m, 186, main, 100, div('New community programme opens its doors'), heading(F, C, 28, { autofit: 'shrink' })),
      textBox(m, 288, main, 30, div(`By ${bi(info, 'name')}, ${bi(info, 'title')}`), body(F, C, 11.5, { fontStyle: 'italic', color: C.secondaryText })),
      textBox(m, 318, main, 640, paras(...COPY.newsLead), body(F, C, 12.5, { columns: 2, columnGap: 22, textAlign: 'justify', lineHeight: 1.5 })),
      picture(m, 580, 236, 170, 'team meeting office people', { wrapStyle: 'square', wrapDistance: 12 }),
      textBox(sideX, 186, sideW, 290, div('In this issue', 'letter-spacing: 3px; font-size: 11px') + div('<br>') + paras('Programme launch <b>1</b>', 'Volunteer news <b>2</b>', 'Fundraising dinner <b>2</b>', 'Library partnership <b>2</b>', 'Contact us <b>2</b>'), body(F, C, 12.5, { color: C.dark, background: { type: 'solid', color: C.light }, paddingLeft: 16, paddingRight: 16, paddingTop: 14, paddingBottom: 14, lineHeight: 1.9 })),
      textBox(sideX, 494, sideW, 200, div('Save the date', 'letter-spacing: 3px; font-size: 11px') + div('<br>') + div('<b>Annual dinner</b>') + div('Friday, 24 October') + div('<br>') + div('<b>Open workshop</b>') + div('Every Tuesday, 6 PM'), body(F, C, 12.5, { color: C.onSecondary, background: { type: 'solid', color: C.secondary }, paddingLeft: 16, paddingRight: 16, paddingTop: 14, paddingBottom: 14, lineHeight: 1.6 })),
      picture(sideX, 712, sideW, 200, 'city skyline sunset'),
      textBox(sideX, 916, sideW, 46, div('Our neighbourhood, seen from the new rooftop workspace.'), body(F, C, 10, { fontStyle: 'italic', color: C.secondaryText })),
    ];
    const col = (cw - 20) / 2;
    const page2 = [
      textBox(m, 44, cw, 44, div('Inside the organisation'), heading(F, C, 26)),
      rect(m, 92, cw, 2, C.primary),
      textBox(m, 108, col, 60, div('Volunteer team grows by forty percent'), heading(F, C, 19)),
      textBox(m, 170, col, 150, div(COPY.story1), body(F, C, 12.5, { lineHeight: 1.5 })),
      textBox(m + col + 20, 108, col, 60, div('Fundraising dinner moves to a larger venue'), heading(F, C, 19)),
      textBox(m + col + 20, 170, col, 150, div(COPY.story2), body(F, C, 12.5, { lineHeight: 1.5 })),
      picture(m, 336, col, 210, 'volunteers smiling group'),
      picture(m + col + 20, 336, col, 210, 'gala dinner table setting'),
      textBox(m, 566, cw, 90, div(COPY.quote), heading(F, C, 22, { fontStyle: 'italic', fontWeight: 'normal', textAlign: 'center', verticalAlign: 'middle', color: C.primary, background: { type: 'solid', color: C.light } })),
      textBox(m, 676, col, 40, div('Library partnership'), heading(F, C, 19)),
      textBox(m, 718, col, 150, div(COPY.story3), body(F, C, 12.5, { lineHeight: 1.5 })),
      textBox(m + col + 20, 676, col, 192, div('Contact us', 'letter-spacing: 3px; font-size: 11px') + div('<br>') + paras(`<b>${bi(info, 'organization')}</b>`, ...biLines(info, 'address'), bi(info, 'phone'), bi(info, 'email'), bi(info, 'website')), body(F, C, 12, { color: C.onDark, background: { type: 'solid', color: C.dark }, paddingLeft: 16, paddingRight: 16, paddingTop: 14, paddingBottom: 14, lineHeight: 1.6 })),
    ];
    return { master, pages: [{ name: 'Front page', objects: page1 }, { name: 'Page 2', objects: page2 }] };
  },
};

const newsletterModern = {
  id: 'newsletter-modern', category: 'newsletter', name: 'Modern Newsletter', preset: 'letter', pageCount: 2,
  description: 'Colour-block masthead, edge-to-edge feature photo, three-column story and bold statistics on page two.',
  build: ({ C, F, info }, { W, H }) => {
    const m = 48;
    const cw = W - m * 2;
    const page1 = [
      rect(0, 0, W, 190, C.primary),
      textBox(m, 42, cw - 220, 80, div(bi(info, 'organization')), heading(F, C, 44, { color: C.onPrimary, autofit: 'shrink', verticalAlign: 'middle' })),
      textBox(m, 122, cw - 220, 40, div(bi(info, 'tagline')), body(F, C, 14, { color: readableOn(C.primary), fontStyle: 'italic' })),
      textBox(W - m - 180, 40, 180, 110, div('Issue', 'font-size: 11px; letter-spacing: 3px') + div('01') + div('This month', 'font-size: 11px; letter-spacing: 2px'), heading(F, C, 40, { color: C.onDark, textAlign: 'center', verticalAlign: 'middle', background: { type: 'solid', color: C.dark }, lineHeight: 1.05 })),
      picture(0, 190, W, 300, 'modern architecture office building'),
      textBox(m, 506, cw, 76, div('New community programme opens its doors to a full house'), heading(F, C, 28, { autofit: 'shrink' })),
      textBox(m, 586, cw, 318, paras(...COPY.newsLead), body(F, C, 12, { columns: 3, columnGap: 24, lineHeight: 1.5 })),
      textBox(m, 920, cw, 90, div('Upcoming', 'letter-spacing: 3px; font-size: 11px; font-weight: bold') + div(`<b>Annual dinner</b> · Friday 24 October &nbsp;&nbsp; <b>Open workshop</b> · Tuesdays 6 PM &nbsp;&nbsp; <b>Showcase</b> · First Saturday monthly`), body(F, C, 12.5, { color: C.onSecondary, background: { type: 'solid', color: C.secondary }, paddingLeft: 18, paddingRight: 18, paddingTop: 12, paddingBottom: 12, verticalAlign: 'middle', lineHeight: 1.7 })),
    ];
    const col = (cw - 24) / 2;
    const stats = [['240', 'guests on opening night'], ['40%', 'more volunteers this quarter'], ['12', 'weekly workshops']];
    const statW = (cw - 40) / 3;
    const page2 = [
      rect(0, 0, W, 64, C.dark),
      textBox(m, 0, cw, 64, div('News & updates'), heading(F, C, 20, { color: C.onDark, verticalAlign: 'middle', textTransform: 'uppercase', letterSpacing: 3 })),
      picture(m, 96, col, 210, 'volunteers smiling group'),
      textBox(m, 316, col, 50, div('Volunteer team grows by forty percent'), heading(F, C, 18)),
      textBox(m, 366, col, 150, div(COPY.story1), body(F, C, 12, { lineHeight: 1.5 })),
      picture(m + col + 24, 96, col, 210, 'gala dinner table setting'),
      textBox(m + col + 24, 316, col, 50, div('Fundraising dinner moves to a larger venue'), heading(F, C, 18)),
      textBox(m + col + 24, 366, col, 150, div(COPY.story2), body(F, C, 12, { lineHeight: 1.5 })),
      rect(m, 540, cw, 1, C.secondary),
      ...stats.flatMap(([n, t], i) => {
        const x = m + i * (statW + 20);
        return [
          textBox(x, 560, statW, 70, div(n), heading(F, C, 48, { color: C.primary, textAlign: 'center', verticalAlign: 'middle' })),
          textBox(x, 630, statW, 40, div(t), label(F, C, 10, { textAlign: 'center' })),
        ];
      }),
      textBox(m, 700, col, 40, div('Library partnership'), heading(F, C, 18)),
      textBox(m, 742, col, 160, div(COPY.story3), body(F, C, 12, { lineHeight: 1.5 })),
      textBox(m + col + 24, 700, col, 202, div('Contact us', 'letter-spacing: 3px; font-size: 11px; font-weight: bold') + div('<br>') + paras(`<b>${bi(info, 'organization')}</b>`, ...biLines(info, 'address'), bi(info, 'phone'), bi(info, 'email'), bi(info, 'website')), body(F, C, 12, { color: C.dark, background: { type: 'solid', color: C.light }, paddingLeft: 16, paddingRight: 16, paddingTop: 14, paddingBottom: 14, lineHeight: 1.6 })),
      rect(0, H - 40, W, 40, C.primary),
      textBox(m, H - 40, cw, 40, div(`${bi(info, 'website')} · ${bi(info, 'email')}`), body(F, C, 11, { color: C.onPrimary, textAlign: 'center', verticalAlign: 'middle' })),
    ];
    return { pages: [{ name: 'Front page', objects: page1 }, { name: 'Page 2', objects: page2 }] };
  },
};

const cardMinimal = {
  id: 'card-minimal', category: 'card', name: 'Minimal Business Card', preset: 'businesscard', pageCount: 2,
  description: 'Clean typography with a single accent line. The back carries your organisation name on a solid colour field.',
  build: ({ C, F, info }, { W, H }) => ({
    pages: [
      { name: 'Front', objects: [
        textBox(W - 140, 18, 120, 28, div(bi(info, 'organization')), body(F, C, 9.5, { fontWeight: 'bold', color: C.primary, textAlign: 'right', autofit: 'shrink' })),
        textBox(20, 44, 220, 40, div(bi(info, 'name')), heading(F, C, 19, { autofit: 'shrink', verticalAlign: 'middle' })),
        textBox(20, 80, 220, 22, div(bi(info, 'title')), label(F, C, 8.5)),
        rect(26, 108, 40, 3, C.primary),
        textBox(20, 116, 300, 66, paras(bi(info, 'phone'), bi(info, 'email'), bi(info, 'website')), body(F, C, 9, { lineHeight: 1.6, color: C.dark })),
      ] },
      { name: 'Back', background: { type: 'solid', color: C.primary }, objects: [
        textBox(24, 60, W - 48, 50, div(bi(info, 'organization')), heading(F, C, 22, { color: C.onPrimary, textAlign: 'center', verticalAlign: 'middle', autofit: 'shrink' })),
        textBox(24, 110, W - 48, 30, div(bi(info, 'tagline')), body(F, C, 9, { color: readableOn(C.primary), textAlign: 'center', fontStyle: 'italic' })),
      ] },
    ],
  }),
};

const cardBold = {
  id: 'card-bold', category: 'card', name: 'Bold Business Card', preset: 'businesscard', pageCount: 2,
  description: 'A solid colour panel with your initial in a circle, details on the right, and a dark back with the website.',
  build: ({ C, F, info }, { W, H }) => {
    const initial = bi(info, 'organization').charAt(0).toUpperCase();
    return {
      pages: [
        { name: 'Front', objects: [
          rect(0, 0, 120, H, C.primary),
          shape('circle', 30, 56, 60, 60, { fill: { type: 'solid', color: '#ffffff' } }),
          textBox(30, 56, 60, 60, div(initial), heading(F, C, 28, { color: C.primary, textAlign: 'center', verticalAlign: 'middle' })),
          textBox(20, 128, 80, 40, div(bi(info, 'organization')), body(F, C, 8, { color: C.onPrimary, textAlign: 'center', fontWeight: 'bold', autofit: 'shrink' })),
          textBox(134, 34, 190, 34, div(bi(info, 'name')), heading(F, C, 17, { autofit: 'shrink', verticalAlign: 'middle' })),
          textBox(134, 66, 190, 20, div(bi(info, 'title')), label(F, C, 8, { letterSpacing: 1.5 })),
          rect(140, 92, 28, 2, C.primary),
          textBox(134, 98, 190, 74, paras(bi(info, 'phone'), bi(info, 'email'), bi(info, 'website'), addressInline(info)), body(F, C, 8.5, { lineHeight: 1.55, color: C.dark })),
        ] },
        { name: 'Back', background: { type: 'solid', color: C.dark }, objects: [
          shape('circle', W / 2 - 36, 40, 72, 72, { fill: { type: 'solid', color: C.primary } }),
          textBox(W / 2 - 36, 40, 72, 72, div(initial), heading(F, C, 34, { color: C.onPrimary, textAlign: 'center', verticalAlign: 'middle' })),
          textBox(24, 120, W - 48, 30, div(bi(info, 'organization')), heading(F, C, 14, { color: C.onDark, textAlign: 'center', autofit: 'shrink' })),
          textBox(24, 148, W - 48, 24, div(bi(info, 'website')), body(F, C, 9, { color: readableOn(C.dark), textAlign: 'center', letterSpacing: 1 })),
        ] },
      ],
    };
  },
};

const cardElegant = {
  id: 'card-elegant', category: 'card', name: 'Elegant Business Card', preset: 'businesscard', pageCount: 2,
  description: 'Centred layout inside a fine border with small caps and hairline rules — a classic, refined look.',
  build: ({ C, F, info }, { W, H }) => ({
    pages: [
      { name: 'Front', objects: [
        shape('rectangle', 12, 12, W - 24, H - 24, { fill: { type: 'none' }, stroke: { color: C.primary, width: 1 } }),
        textBox(24, 40, W - 48, 40, div(bi(info, 'name')), heading(F, C, 19, { textAlign: 'center', verticalAlign: 'middle', autofit: 'shrink' })),
        rect(W / 2 - 30, 84, 60, 1, C.primary),
        textBox(24, 90, W - 48, 20, div(bi(info, 'title')), label(F, C, 8, { textAlign: 'center', letterSpacing: 3 })),
        textBox(24, 122, W - 48, 44, div(`${bi(info, 'phone')} · ${bi(info, 'email')}`) + div(bi(info, 'website')), body(F, C, 8.5, { textAlign: 'center', lineHeight: 1.7, color: C.dark })),
      ] },
      { name: 'Back', background: { type: 'solid', color: C.pale }, objects: [
        shape('rectangle', 12, 12, W - 24, H - 24, { fill: { type: 'none' }, stroke: { color: C.primary, width: 1 } }),
        textBox(24, 62, W - 48, 40, div(bi(info, 'organization')), heading(F, C, 18, { textAlign: 'center', verticalAlign: 'middle', autofit: 'shrink' })),
        textBox(24, 104, W - 48, 28, div(bi(info, 'tagline')), body(F, C, 9, { textAlign: 'center', fontStyle: 'italic', color: C.secondaryText })),
      ] },
    ],
  }),
};

const postcardAnnounce = {
  id: 'postcard-announce', category: 'postcard', name: 'Announcement Postcard', preset: 'postcard', pageCount: 2,
  description: 'Photo front with a colour band headline; the back has a message area, address block and stamp box.',
  build: ({ C, F, info }, { W, H }) => ({
    pages: [
      { name: 'Front', objects: [
        picture(0, 0, W, H, 'storefront grand opening ribbon'),
        rect(0, H - 120, W, 120, C.primary, { opacity: 92 }),
        textBox(28, H - 112, W - 56, 62, div('Grand Opening'), heading(F, C, 38, { color: C.onPrimary, verticalAlign: 'middle', autofit: 'shrink' })),
        textBox(28, H - 52, W - 56, 36, div(`Saturday 10 AM · ${addressInline(info)}`), body(F, C, 12, { color: readableOn(C.primary) })),
      ] },
      { name: 'Back', objects: [
        textBox(24, 24, 280, H - 48, paras(...COPY.postcardMsg.split('\n')) + div('<br>') + div(`<b>${bi(info, 'organization')}</b>`), body(F, C, 11, { lineHeight: 1.5 })),
        rect(316, 24, 1, H - 48, C.secondary),
        shape('rectangle', W - 90, 24, 66, 80, { fill: { type: 'none' }, stroke: { color: C.secondary, width: 1, dash: 'dashed' } }),
        textBox(W - 90, 24, 66, 80, div('Stamp'), label(F, C, 7, { textAlign: 'center', verticalAlign: 'middle' })),
        textBox(336, 190, W - 360, 110, paras('Recipient Name', 'Street Address', 'City, State ZIP'), body(F, C, 12, { lineHeight: 1.8, color: C.dark })),
        textBox(336, 120, W - 360, 40, div(bi(info, 'website')), body(F, C, 10, { color: C.secondaryText })),
      ] },
    ],
  }),
};

/* ------------------------------------------------------------------ */
/* Shared pieces for the brochure / menu / certificate families       */
/* ------------------------------------------------------------------ */

const COPY2 = {
  about: [
    'We are a team of specialists who believe good work starts with listening. For more than a decade we have helped organisations of every size plan, design and deliver projects that make a measurable difference.',
    'Our approach is simple: understand the goal, involve the right people early and keep things clear from the first conversation to the final handover.',
  ],
  services: [
    ['Consulting', 'Strategy, audits and roadmaps that turn ambition into a plan your whole team can follow.', ['Discovery workshops', 'Market and competitor review', 'Twelve-month roadmap', 'Quarterly check-ins'], 'business consulting meeting'],
    ['Design', 'Brand identities, print and digital design that look sharp and work hard wherever they appear.', ['Logo and identity systems', 'Brochures, signage and packaging', 'Website and social templates', 'Brand guidelines'], 'graphic designer workspace'],
    ['Support', 'Friendly, responsive help that keeps everything running long after launch day.', ['Same-day response', 'Monthly performance report', 'Training for your team', 'Priority upgrades'], 'customer support team headset'],
  ],
  whyUs: ['Fixed-price quotes, no surprises', 'A named contact from day one', 'Ten years of happy clients', 'Local team, global standards'],
  productIntro: 'Meet the newest member of the family: quieter, lighter and made to last. Every detail has been reconsidered, from the recycled aluminium body to the packaging it arrives in.',
  features: [
    ['Built to last', 'A machined aluminium frame, replaceable parts and a five-year guarantee mean it is the last one you will need to buy.'],
    ['Effortless setup', 'Unbox, plug in and go. Guided setup takes under three minutes and the companion app keeps everything up to date.'],
    ['Sustainably made', 'Made with 80% recycled materials in a carbon-neutral facility and shipped in plastic-free packaging.'],
  ],
  specs: [['Dimensions', '24 × 18 × 6 cm'], ['Weight', '1.2 kg'], ['Materials', 'Recycled aluminium'], ['Power', '20 W USB-C'], ['Colours', 'Sand · Slate · Forest'], ['Guarantee', '5 years']],
  prices: [['Standard edition', '149.00'], ['Pro edition', '229.00'], ['Accessory kit', '39.00'], ['Extended care (3 yrs)', '49.00']],
  profileIntro: 'Founded by two friends with a shared workshop and a single client, we have grown into a team of forty people serving customers across the region. What has not changed is the way we work: carefully, honestly and always in partnership.',
  milestones: [['2012', 'Founded in a converted garage studio'], ['2015', 'First national client and tenth team member'], ['2019', 'Moved to the Main Street headquarters'], ['2023', 'Certified B Corporation'], ['Today', 'Forty people, three hundred clients']],
  values: [['People first', 'We hire for kindness and curiosity, then train for everything else.'], ['Do it properly', 'Good enough is not good enough. We finish what we start.'], ['Give back', 'One percent of revenue and one day a month go to local causes.']],
  restaurantMenu: {
    Starters: [['Burrata & heritage tomatoes', '12.50', 'basil oil, aged balsamic, grilled sourdough'], ['Crispy calamari', '11.00', 'lemon aioli, pickled chilli'], ['Roasted beetroot salad', '10.50', 'goat curd, candied walnuts, watercress'], ['Soup of the day', '8.00', 'served with warm bread and salted butter']],
    Mains: [['Pan-roasted sea bass', '26.00', 'crushed potatoes, samphire, brown butter'], ['Braised beef short rib', '28.50', 'celeriac purée, red wine jus, crispy shallots'], ['Wild mushroom risotto', '19.50', 'parmesan, truffle oil, herb salad'], ['Free-range chicken supreme', '23.00', 'charred leeks, tarragon cream'], ['Aubergine parmigiana', '18.00', 'tomato, mozzarella, basil']],
    Desserts: [['Dark chocolate tart', '9.50', 'sea salt caramel, crème fraîche'], ['Lemon posset', '8.50', 'shortbread, raspberries'], ['Affogato', '7.00', 'vanilla gelato, espresso, amaretti'], ['Cheese board', '13.00', 'three regional cheeses, chutney, crackers']],
    Drinks: [['House red · white · rosé', '7.50 / 32.00', '175 ml glass / bottle'], ['Prosecco', '9.00 / 38.00', 'glass / bottle'], ['Craft beer', '6.50', 'ask for today\u2019s selection'], ['Fresh lemonade', '4.50', 'mint, cucumber']],
  },
  cafeMenu: {
    'Coffee': [['Espresso', '3.00'], ['Americano', '3.50'], ['Flat white', '4.20'], ['Cappuccino', '4.20'], ['Latte', '4.40'], ['Mocha', '4.80'], ['Cold brew', '4.50']],
    'Tea & more': [['Loose-leaf tea', '3.20'], ['Chai latte', '4.40'], ['Hot chocolate', '4.20'], ['Matcha latte', '4.80'], ['Fresh juice', '4.90']],
    'Breakfast': [['Granola, yoghurt & berries', '8.50'], ['Avocado toast', '9.50'], ['Eggs any style on sourdough', '9.00'], ['Buttermilk pancakes', '10.50'], ['Breakfast burrito', '11.00']],
    'Lunch': [['Soup & sourdough', '8.00'], ['Chicken Caesar wrap', '10.50'], ['Halloumi & roasted veg sandwich', '10.00'], ['Superfood bowl', '12.50'], ['Quiche of the day & salad', '9.50']],
    'Sweet things': [['Cinnamon bun', '3.80'], ['Almond croissant', '3.60'], ['Banana bread', '3.50'], ['Brownie', '3.40'], ['Carrot cake', '4.50']],
  },
  takeawayMenu: [
    ['Pizza', [['Margherita', '11.00'], ['Pepperoni', '13.00'], ['Four cheese', '13.50'], ['Veggie supreme', '13.50'], ['Meat feast', '15.00'], ['BBQ chicken', '14.50']]],
    ['Burgers', [['Classic cheeseburger', '9.50'], ['Bacon & blue cheese', '11.00'], ['Crispy chicken', '10.00'], ['Falafel burger', '9.50'], ['Double stack', '13.00']]],
    ['Sides', [['Fries', '3.50'], ['Sweet potato fries', '4.50'], ['Garlic bread', '4.00'], ['Onion rings', '4.00'], ['Coleslaw', '2.50']]],
    ['Salads', [['Caesar', '8.50'], ['Greek', '8.00'], ['Chicken & avocado', '10.50'], ['Quinoa & roasted veg', '9.50']]],
    ['Drinks', [['Soft drinks', '2.50'], ['Sparkling water', '2.00'], ['Fresh lemonade', '3.50'], ['Milkshakes', '4.50']]],
    ['Desserts', [['Brownie', '4.00'], ['Cheesecake', '5.00'], ['Ice cream (2 scoops)', '4.00'], ['Churros', '5.50']]],
  ],
  achievement: 'in recognition of outstanding performance, dedication and excellence in completing the Leadership Development Programme with distinction.',
  appreciation: 'in grateful appreciation of your generous contribution of time, energy and expertise. Your commitment has made a lasting difference to our community and to everyone we serve.',
  giftTerms: 'This certificate is redeemable for goods or services up to the value shown. It is not redeemable for cash, cannot be replaced if lost or stolen and must be presented at the time of purchase. Valid for 12 months from the date of issue.',
  bulletinLead: [
    'Work on the new community centre reached a milestone this month as the roof went on ahead of schedule. Visitors to the open morning were able to walk through the main hall for the first time and see the light-filled space that will host classes, meetings and celebrations from next spring.',
    'The project has been funded through a mix of grants, local fundraising and hundreds of small donations — a remarkable effort by residents. "Every brick has a story," said the project lead. "This building belongs to the people who made it happen."',
  ],
  bulletinStories: [
    ['Library hours extended', 'From next month the branch library opens until 8 PM on Tuesdays and Thursdays, with a new homework club for secondary students.', 'public library reading books'],
    ['Farmers\u2019 market returns', 'The Saturday market is back on the square from the first weekend of May with twenty stalls, live music and a produce swap.', 'farmers market vegetables stall'],
    ['Repair café volunteers wanted', 'Handy with a soldering iron or a sewing machine? The monthly repair café needs fixers for its autumn season.', 'repair workshop tools hands'],
  ],
  diary: [['Mon 12', 'Board meeting · 7 PM'], ['Sat 17', 'Spring clean-up · 9 AM'], ['Wed 21', 'Open workshop · 6 PM'], ['Sat 24', 'Farmers\u2019 market · 8 AM'], ['Thu 29', 'Book club · 7:30 PM']],
  spotlight: '“I came for one shift and stayed for eight years. The people here are the reason I keep turning up.” — Maria, volunteer since 2016',
  digestLead: [
    'The past quarter was defined by growth in the places we planned for and resilience in the places we did not. Revenue rose eleven percent year on year, the new regional office opened on time, and customer satisfaction reached its highest level since we began measuring it.',
    'None of that happens by accident. It is the result of teams who plan carefully, communicate openly and look after one another when the pressure is on. This issue celebrates a few of them.',
  ],
  digestFeatures: [
    ['Inside the regional launch', 'Six months of planning, forty new colleagues and one very busy opening week. We look at what went right, what we learned and what comes next for the regional team.', 'modern office opening team'],
    ['Customer stories: the long view', 'Three customers who have been with us for more than a decade explain what keeps them coming back — and what they would still like us to improve.', 'business handshake meeting'],
  ],
  digestStats: [['+11%', 'revenue growth'], ['94', 'satisfaction score'], ['40', 'new colleagues'], ['3', 'new markets']],
};

/** Logo from Business Information, or a scheme-coloured initial badge when none has been uploaded. */
const logoBlock = (info, x, y, size, C, F, { onDark = false } = {}) => {
  if (info && info.logo) {
    return [{ id: uid(), type: 'image', x, y, width: size, height: size, rotation: 0, locked: false, visible: true, properties: { src: info.logo, objectFit: 'contain', opacity: 100, borderRadius: 0, wrapStyle: 'none', altText: `${bi(info, 'organization')} logo` } }];
  }
  const fill = onDark ? '#ffffff' : C.primary;
  return [
    shape('circle', x, y, size, size, { fill: { type: 'solid', color: fill } }),
    textBox(x, y, size, size, div(bi(info, 'organization').charAt(0).toUpperCase()), heading(F, C, Math.round(size * 0.48), { color: onDark ? C.primary : C.onPrimary, textAlign: 'center', verticalAlign: 'middle' })),
  ];
};

/** Price list: "name ......... price" lines built on a right-aligned dot-leader tab stop, optional description lines. */
const priceLines = (items, C, { descSize = 10.5, bold = true } = {}) => items.map(([name, price, desc]) => div(`${bold ? `<b>${esc(name)}</b>` : esc(name)}\t${esc(price)}`) + (desc ? div(esc(desc), `font-size: ${descSize}px; font-style: italic; color: ${C.secondaryText}`) : '')).join('');
const priceBox = (x, y, w, h, html, F, C, size = 12, extra = {}) => {
  const padL = extra.paddingLeft ?? 5, padR = extra.paddingRight ?? 5;
  return textBox(x, y, w, h, html, body(F, C, size, { lineHeight: 1.5, tabStops: [{ pos: w - padL - padR - 4, align: 'right', leader: 'dot' }], ...extra }));
};
const sectionTitle = (x, y, w, title, F, C, size = 20, extra = {}) => [textBox(x, y, w, size * 1.8, div(esc(title)), heading(F, C, size, extra)), rect(x + 5, y + size * 1.8 + 2, 40, 2, C.primary)];
/** Fold lines for brochure spreads, with Publisher's ¼ in panel margins on either side of each crease (derived guides). */
const foldGuides = (positions) => positions.map((position) => ({ orientation: 'vertical', position, kind: 'fold', gutter: 24 }));

/* ------------------------------------------------------------------ */
/* Newsletters (more)                                                  */
/* ------------------------------------------------------------------ */

const newsletterBulletin = {
  id: 'newsletter-bulletin', category: 'newsletter', name: 'Community Bulletin', preset: 'letter', pageCount: 2,
  description: 'Dark masthead with an issue badge, a lead story beside an announcements sidebar and a tabbed diary, then three short stories and a volunteer spotlight on page two.',
  build: ({ C, F, info }, { W, H }) => {
    const m = 48;
    const cw = W - m * 2;
    const main = 470;
    const sideX = m + main + 26;
    const sideW = cw - main - 26;
    const master = [
      rect(m, H - 60, cw, 1, C.secondary),
      textBox(m, H - 54, cw, 26, div(`${bi(info, 'organization')} · ${bi(info, 'phone')} · ${bi(info, 'website')}`), body(F, C, 9.5, { color: C.secondaryText, textAlign: 'center' })),
    ];
    const page1 = [
      rect(0, 0, W, 112, C.dark),
      textBox(m, 22, cw - 200, 68, div(bi(info, 'organization')), heading(F, C, 36, { color: C.onDark, verticalAlign: 'middle', autofit: 'shrink' })),
      textBox(m, 82, cw - 200, 24, div('Community bulletin'), label(F, C, 10, { color: tint(C.primary, 0.45), letterSpacing: 3 })),
      rect(W - m - 170, 24, 170, 64, C.primary),
      textBox(W - m - 170, 24, 170, 64, div('Issue 14', 'font-size: 11px; letter-spacing: 3px') + div('Spring'), heading(F, C, 22, { color: C.onPrimary, textAlign: 'center', verticalAlign: 'middle', lineHeight: 1.15 })),
      textBox(m, 136, main, 84, div('New community centre reaches the roof'), heading(F, C, 30, { autofit: 'shrink' })),
      textBox(m, 222, main, 26, div(`By ${bi(info, 'name')} · ${bi(info, 'title')}`), body(F, C, 11, { fontStyle: 'italic', color: C.secondaryText })),
      picture(m, 254, main, 250, 'community centre construction volunteers'),
      textBox(m, 514, main, 330, paras(...COPY2.bulletinLead), body(F, C, 12.5, { columns: 2, columnGap: 22, lineHeight: 1.5, textAlign: 'justify' })),
      textBox(sideX, 136, sideW, 250, div('Announcements', 'letter-spacing: 3px; font-size: 11px') + div('<br>') + paras('<b>Recycling day</b> moves to Thursdays from 1 May.', '<b>Summer fair</b> stall bookings open — see page 2.', '<b>New members</b> welcome evening on the 21st.'), body(F, C, 12, { color: C.dark, background: { type: 'solid', color: C.light }, paddingLeft: 16, paddingRight: 16, paddingTop: 14, paddingBottom: 14, lineHeight: 1.65 })),
      textBox(sideX, 400, sideW, 210, div('Diary', 'letter-spacing: 3px; font-size: 11px; font-weight: bold') + div('<br>') + COPY2.diary.map(([d, t]) => div(`<b>${d}</b>\t${t}`)).join(''), body(F, C, 11.5, { color: C.onSecondary, background: { type: 'solid', color: C.secondary }, paddingLeft: 16, paddingRight: 16, paddingTop: 14, paddingBottom: 14, lineHeight: 1.75, tabStops: [{ pos: 62, align: 'left', leader: 'none' }] })),
      picture(sideX, 624, sideW, 150, 'neighbourhood street trees'),
      textBox(sideX, 780, sideW, 64, div('Get involved', 'letter-spacing: 2px; font-size: 10px; font-weight: bold') + div(`${bi(info, 'email')} · ${bi(info, 'phone')}`), body(F, C, 11, { color: C.dark, lineHeight: 1.6 })),
      rect(m, 866, cw, 3, C.primary),
      textBox(m, 876, cw, 40, div(`Continued inside: library hours, the farmers\u2019 market and a call for repair café volunteers.`), body(F, C, 11.5, { fontStyle: 'italic', color: C.secondaryText, verticalAlign: 'middle' })),
    ];
    const colW = (cw - 40) / 3;
    const page2 = [
      rect(0, 0, W, 56, C.primary),
      textBox(m, 0, cw, 56, div('Around the neighbourhood'), heading(F, C, 18, { color: C.onPrimary, verticalAlign: 'middle', textTransform: 'uppercase', letterSpacing: 3 })),
      ...COPY2.bulletinStories.flatMap(([title, text, q], i) => {
        const x = m + i * (colW + 20);
        return [
          picture(x, 86, colW, 160, q),
          textBox(x, 256, colW, 56, div(esc(title)), heading(F, C, 17)),
          textBox(x, 312, colW, 150, div(esc(text)), body(F, C, 12, { lineHeight: 1.5 })),
        ];
      }),
      rect(m, 480, cw, 1, C.secondary),
      textBox(m, 500, cw, 40, div('Volunteer spotlight'), heading(F, C, 22)),
      picture(m, 550, 180, 180, 'smiling volunteer portrait', { borderRadius: 90 }),
      textBox(m + 204, 556, cw - 204, 170, div(COPY2.spotlight), heading(F, C, 20, { fontStyle: 'italic', fontWeight: 'normal', color: C.primary, verticalAlign: 'middle', lineHeight: 1.4 })),
      textBox(m, 760, (cw - 20) / 2, 170, div('Summer fair · stall bookings', 'letter-spacing: 2px; font-size: 10.5px; font-weight: bold') + div('<br>') + div('Saturday 12 July, 10 AM – 4 PM on the square. Community groups and local makers book free; commercial stalls from 25. Email the events team to reserve a pitch.'), body(F, C, 12, { color: C.dark, background: { type: 'solid', color: C.light }, paddingLeft: 16, paddingRight: 16, paddingTop: 14, paddingBottom: 14, lineHeight: 1.55 })),
      textBox(m + (cw - 20) / 2 + 20, 760, (cw - 20) / 2, 170, div('Contact us', 'letter-spacing: 2px; font-size: 10.5px; font-weight: bold') + div('<br>') + paras(`<b>${bi(info, 'organization')}</b>`, ...biLines(info, 'address'), bi(info, 'phone'), bi(info, 'email')), body(F, C, 12, { color: C.onDark, background: { type: 'solid', color: C.dark }, paddingLeft: 16, paddingRight: 16, paddingTop: 14, paddingBottom: 14, lineHeight: 1.55 })),
    ];
    return { master, pages: [{ name: 'Front page', objects: page1 }, { name: 'Page 2', objects: page2 }] };
  },
};

const newsletterDigest = {
  id: 'newsletter-digest', category: 'newsletter', name: 'Executive Digest', preset: 'letter', pageCount: 2,
  description: 'Magazine-style cover with a full-bleed photo and headline plate, a dot-leader contents list and a director\u2019s note; features, a numbers column and diary dates on page two.',
  build: ({ C, F, info }, { W, H }) => {
    const m = 48;
    const cw = W - m * 2;
    const master = [
      textBox(m, H - 50, cw, 24, div(`${bi(info, 'organization')} · Executive Digest · ${bi(info, 'website')}`), body(F, C, 9.5, { color: C.secondaryText, textAlign: 'center' })),
    ];
    const contents = [['Inside the regional launch', '2'], ['Customer stories: the long view', '2'], ['By the numbers', '2'], ['Dates for your diary', '2']];
    const page1 = [
      picture(0, 0, W, 600, 'city skyline aerial business district'),
      rect(m, 40, 210, 44, C.primary),
      textBox(m, 40, 210, 44, div(bi(info, 'organization')), body(F, C, 12, { color: C.onPrimary, fontWeight: 'bold', textAlign: 'center', verticalAlign: 'middle', autofit: 'shrink' })),
      rect(0, 440, W, 160, C.dark, { opacity: 90 }),
      textBox(m, 452, cw, 30, div('Quarterly digest · Q2'), label(F, C, 10.5, { color: tint(C.primary, 0.45), letterSpacing: 4 })),
      textBox(m, 482, cw, 106, div('A quarter of growth, on our own terms'), heading(F, C, 40, { color: C.onDark, autofit: 'shrink', verticalAlign: 'middle' })),
      ...sectionTitle(m, 632, 300, 'In this issue', F, C, 20),
      textBox(m, 684, 300, 170, contents.map(([t, pg]) => div(`${t}\t${pg}`)).join(''), body(F, C, 12.5, { lineHeight: 1.9, tabStops: [{ pos: 300 - 12, align: 'right', leader: 'dot' }] })),
      rect(m + 340, 632, 1, 300, C.secondary),
      textBox(m + 364, 632, cw - 364, 40, div(`A note from ${bi(info, 'name')}`), heading(F, C, 20)),
      textBox(m + 364, 676, cw - 364, 250, paras(...COPY2.digestLead), body(F, C, 12, { lineHeight: 1.55 })),
      textBox(m + 364, 900, cw - 364, 30, div(`${bi(info, 'name')} · ${bi(info, 'title')}`), body(F, C, 11, { fontStyle: 'italic', color: C.secondaryText })),
      rect(m, 950, cw, 3, C.primary),
    ];
    const mainW = 470;
    const sideX = m + mainW + 26;
    const sideW = cw - mainW - 26;
    const page2 = [
      rect(0, 0, W, 8, C.primary),
      ...COPY2.digestFeatures.flatMap(([title, text, q], i) => {
        const y = 40 + i * 420;
        return [
          picture(m, y, mainW, 200, q),
          textBox(m, y + 212, mainW, 50, div(esc(title)), heading(F, C, 22)),
          textBox(m, y + 262, mainW, 130, div(esc(text)), body(F, C, 12.5, { lineHeight: 1.55 })),
        ];
      }),
      textBox(sideX, 40, sideW, 30, div('By the numbers'), label(F, C, 11, { letterSpacing: 3 })),
      ...COPY2.digestStats.flatMap(([n, t], i) => {
        const y = 78 + i * 118;
        return [
          rect(sideX, y, sideW, 100, i % 2 ? C.light : C.pale),
          textBox(sideX, y + 6, sideW, 58, div(n), heading(F, C, 34, { color: C.primary, textAlign: 'center', verticalAlign: 'middle' })),
          textBox(sideX, y + 62, sideW, 30, div(t), label(F, C, 9.5, { textAlign: 'center' })),
        ];
      }),
      textBox(sideX, 560, sideW, 330, div('Dates for your diary', 'letter-spacing: 2px; font-size: 10.5px; font-weight: bold') + div('<br>') + COPY2.diary.map(([d, t]) => div(`<b>${d}</b>\t${t}`)).join('') + div('<br>') + div(`Full calendar at ${bi(info, 'website')}`, `font-size: 10.5px; font-style: italic`), body(F, C, 11.5, { color: C.onDark, background: { type: 'solid', color: C.dark }, paddingLeft: 16, paddingRight: 16, paddingTop: 14, paddingBottom: 14, lineHeight: 1.8, tabStops: [{ pos: 62, align: 'left', leader: 'none' }] })),
      rect(m, 900, cw, 1, C.secondary),
      textBox(m, 912, cw, 60, div(`<b>${bi(info, 'organization')}</b> · ${addressInline(info)} · ${bi(info, 'phone')} · ${bi(info, 'email')}`), body(F, C, 11, { color: C.dark, textAlign: 'center', verticalAlign: 'middle' })),
    ];
    return { master, pages: [{ name: 'Cover', objects: page1 }, { name: 'Page 2', objects: page2 }] };
  },
};

/* ------------------------------------------------------------------ */
/* Brochures (Letter landscape)                                        */
/* ------------------------------------------------------------------ */

const brochureTrifoldServices = {
  id: 'brochure-trifold-services', category: 'brochure', name: 'Tri-fold Services Brochure', preset: 'letter', landscape: true, pageCount: 2,
  description: 'Classic tri-fold on landscape Letter: outside panels (inside flap, back cover, front cover) with fold guides, and three inside service panels. Fold along the guides.',
  build: ({ C, F, info }, { W, H }) => {
    const P = W / 3;
    const m = 28;
    const pw = P - m * 2;
    const front = P * 2;
    const back = P;
    const outside = [
      // Front cover
      rect(front, 0, P, H, C.primary),
      picture(front + m, 56, pw, 320, 'modern office team consulting', { stroke: { color: '#ffffff', width: 4 } }),
      textBox(front + m, 400, pw, 112, div(bi(info, 'organization')), heading(F, C, 34, { color: C.onPrimary, autofit: 'shrink', verticalAlign: 'middle' })),
      textBox(front + m, 516, pw, 60, div(bi(info, 'tagline')), body(F, C, 14, { color: readableOn(C.primary), fontStyle: 'italic' })),
      rect(front + m, 592, 60, 3, readableOn(C.primary)),
      textBox(front + m, 606, pw, 40, div('Services & solutions'), label(F, C, 11, { color: readableOn(C.primary), letterSpacing: 4 })),
      textBox(front + m, H - 92, pw, 56, div(bi(info, 'website')) + div(bi(info, 'phone')), body(F, C, 12, { color: C.onPrimary, lineHeight: 1.6 })),
      // Back cover
      ...logoBlock(info, back + P / 2 - 50, 72, 100, C, F),
      textBox(back + m, 190, pw, 44, div(bi(info, 'organization')), heading(F, C, 20, { textAlign: 'center', autofit: 'shrink' })),
      textBox(back + m, 236, pw, 130, paras(...biLines(info, 'address'), bi(info, 'phone'), bi(info, 'email'), bi(info, 'website')), body(F, C, 12, { textAlign: 'center', lineHeight: 1.7, color: C.dark })),
      rect(back + m, 384, pw, 1, C.secondary),
      textBox(back + m, 398, pw, 28, div('Find us'), label(F, C, 10, { textAlign: 'center' })),
      picture(back + m, 432, pw, 220, 'city street map illustration', { placeholderLabel: 'Map' }),
      textBox(back + m, 668, pw, 60, div('Opening hours') + div('Monday – Friday, 9 AM – 6 PM'), body(F, C, 11.5, { textAlign: 'center', color: C.secondaryText, lineHeight: 1.6 })),
      // Inside flap
      rect(0, 0, P, 210, C.light),
      textBox(m, 44, pw, 56, div('About us'), heading(F, C, 26)),
      rect(m + 4, 104, 48, 3, C.primary),
      textBox(m, 120, pw, 70, div(bi(info, 'tagline')), body(F, C, 13.5, { fontStyle: 'italic', color: C.primary })),
      textBox(m, 236, pw, 300, paras(...COPY2.about), body(F, C, 12, { lineHeight: 1.55 })),
      textBox(m, 560, pw, 190, div('Why choose us', 'letter-spacing: 2px; font-size: 10.5px; font-weight: bold') + div('<br>') + paras(...COPY2.whyUs.map((t) => `• ${t}`)), body(F, C, 12, { color: C.onSecondary, background: { type: 'solid', color: C.secondary }, paddingLeft: 16, paddingRight: 16, paddingTop: 14, paddingBottom: 14, lineHeight: 1.7 })),
    ];
    const inside = [
      ...COPY2.services.flatMap(([title, text, includes, q], i) => {
        const x = i * P + m;
        return [
          textBox(x, 40, pw, 46, div(esc(title)), heading(F, C, 22)),
          rect(x + 4, 90, 48, 3, C.primary),
          picture(x, 108, pw, 190, q),
          textBox(x, 312, pw, 150, div(esc(text)), body(F, C, 12, { lineHeight: 1.55 })),
          textBox(x, 470, pw, 220, div('Includes', 'letter-spacing: 2px; font-size: 10.5px; font-weight: bold') + div('<br>') + paras(...includes.map((t) => `• ${t}`)), body(F, C, 12, { color: C.dark, background: { type: 'solid', color: C.light }, paddingLeft: 16, paddingRight: 16, paddingTop: 14, paddingBottom: 14, lineHeight: 1.7 })),
        ];
      }),
      rect(0, H - 56, W, 56, C.dark),
      textBox(m, H - 56, W - m * 2, 56, div(`<b>${bi(info, 'organization')}</b> · ${bi(info, 'phone')} · ${bi(info, 'email')} · ${bi(info, 'website')}`), body(F, C, 12, { color: C.onDark, textAlign: 'center', verticalAlign: 'middle' })),
    ];
    return { guides: foldGuides([P, P * 2]), pages: [{ name: 'Outside', objects: outside }, { name: 'Inside', objects: inside }] };
  },
};

const brochureTrifoldProduct = {
  id: 'brochure-trifold-product', category: 'brochure', name: 'Tri-fold Product Brochure', preset: 'letter', landscape: true, pageCount: 2,
  description: 'Photo-led tri-fold for a product launch: cover photo with a dark plate, a specifications and price list on the flap, an edge-to-edge hero inside with three numbered features and an order band.',
  build: ({ C, F, info }, { W, H }) => {
    const P = W / 3;
    const m = 28;
    const pw = P - m * 2;
    const front = P * 2;
    const back = P;
    const outside = [
      // Front cover
      picture(front, 0, P, 470, 'minimal product photography studio'),
      rect(front, 440, P, H - 440, C.dark),
      textBox(front + m, 466, pw, 40, div('New for this season'), label(F, C, 10.5, { color: tint(C.primary, 0.45), letterSpacing: 4 })),
      textBox(front + m, 506, pw, 120, div('The Series One'), heading(F, C, 40, { color: C.onDark, autofit: 'shrink', verticalAlign: 'middle' })),
      textBox(front + m, 630, pw, 70, div('Quieter. Lighter. Built to last a lifetime.'), body(F, C, 14, { color: readableOn(C.dark), fontStyle: 'italic' })),
      textBox(front + m, H - 80, pw, 44, div(bi(info, 'organization')) + div(bi(info, 'website')), body(F, C, 11.5, { color: C.onDark, lineHeight: 1.6 })),
      // Back cover
      rect(back, 0, P, H, C.light),
      ...logoBlock(info, back + P / 2 - 44, 64, 88, C, F),
      textBox(back + m, 168, pw, 44, div(bi(info, 'organization')), heading(F, C, 20, { textAlign: 'center', autofit: 'shrink' })),
      textBox(back + m, 210, pw, 40, div(bi(info, 'tagline')), body(F, C, 12, { textAlign: 'center', fontStyle: 'italic', color: C.secondaryText })),
      picture(back + m, 270, pw, 200, 'product in use lifestyle home'),
      textBox(back + m, 490, pw, 130, paras(`<b>Order or ask a question</b>`, bi(info, 'phone'), bi(info, 'email'), bi(info, 'website')), body(F, C, 12, { textAlign: 'center', lineHeight: 1.7, color: C.dark })),
      rect(back + m, 636, pw, 1, C.secondary),
      textBox(back + m, 650, pw, 100, paras(...biLines(info, 'address')), body(F, C, 11, { textAlign: 'center', color: C.secondaryText, lineHeight: 1.6 })),
      // Inside flap: specs + prices
      textBox(m, 44, pw, 46, div('Specifications'), heading(F, C, 22)),
      rect(m + 4, 92, 48, 3, C.primary),
      priceBox(m, 108, pw, 230, COPY2.specs.map(([k, v]) => div(`${k}\t<b>${v}</b>`)).join(''), F, C, 12, { lineHeight: 1.9 }),
      textBox(m, 356, pw, 46, div('Prices'), heading(F, C, 22)),
      rect(m + 4, 404, 48, 3, C.primary),
      priceBox(m, 420, pw, 150, priceLines(COPY2.prices, C), F, C, 12.5, { lineHeight: 1.9 }),
      textBox(m, 590, pw, 160, div('Five-year guarantee', 'letter-spacing: 2px; font-size: 10.5px; font-weight: bold') + div('<br>') + div('Every product is covered against defects for five years. Register online within 30 days to activate.'), body(F, C, 12, { color: C.onSecondary, background: { type: 'solid', color: C.secondary }, paddingLeft: 16, paddingRight: 16, paddingTop: 14, paddingBottom: 14, lineHeight: 1.55 })),
    ];
    const inside = [
      picture(0, 0, W, 290, 'product lifestyle photography wide'),
      rect(0, 290, W, 6, C.primary),
      textBox(m, 316, W - m * 2, 60, div(COPY2.productIntro), body(F, C, 14, { textAlign: 'center', fontStyle: 'italic', color: C.dark, verticalAlign: 'middle' })),
      ...COPY2.features.flatMap(([title, text], i) => {
        const x = i * P + m;
        return [
          shape('circle', x, 404, 44, 44, { fill: { type: 'solid', color: C.primary } }),
          textBox(x, 404, 44, 44, div(String(i + 1)), heading(F, C, 20, { color: C.onPrimary, textAlign: 'center', verticalAlign: 'middle' })),
          textBox(x + 56, 400, pw - 56, 52, div(esc(title)), heading(F, C, 20, { verticalAlign: 'middle' })),
          textBox(x, 466, pw, 120, div(esc(text)), body(F, C, 12.5, { lineHeight: 1.55 })),
        ];
      }),
      textBox(m, 604, W - m * 2, 64, div('In the box', 'letter-spacing: 3px; font-size: 10.5px; font-weight: bold') + div('Series One · USB-C cable · Quick-start guide · Five-year care card · Sticker sheet'), body(F, C, 12.5, { color: C.dark, background: { type: 'solid', color: C.light }, textAlign: 'center', verticalAlign: 'middle', lineHeight: 1.7 })),
      rect(0, H - 120, W, 120, C.strong),
      textBox(m, H - 110, W - m * 2, 50, div('Order today'), heading(F, C, 26, { color: C.onStrong, textAlign: 'center', verticalAlign: 'middle' })),
      textBox(m, H - 60, W - m * 2, 44, div(`${bi(info, 'phone')} · ${bi(info, 'website')} · Free delivery on orders over 100`), body(F, C, 13, { color: C.onStrong, textAlign: 'center', verticalAlign: 'middle' })),
    ];
    return { guides: foldGuides([P, P * 2]), pages: [{ name: 'Outside', objects: outside }, { name: 'Inside', objects: inside }] };
  },
};

const brochureBifoldProfile = {
  id: 'brochure-bifold-profile', category: 'brochure', name: 'Bi-fold Company Profile', preset: 'letter', landscape: true, pageCount: 2,
  description: 'Half-fold company profile: a dark cover with your logo, a contact back panel, and inside pages with your story, a milestone timeline, values and key numbers.',
  build: ({ C, F, info }, { W, H }) => {
    const P = W / 2;
    const m = 40;
    const pw = P - m * 2;
    const outside = [
      // Front (right half)
      rect(P, 0, P, H, C.dark),
      ...logoBlock(info, P + m, 56, 84, C, F, { onDark: true }),
      textBox(P + m, 168, pw, 130, div(bi(info, 'organization')), heading(F, C, 42, { color: C.onDark, autofit: 'shrink', verticalAlign: 'middle' })),
      textBox(P + m, 300, pw, 40, div('Company profile'), label(F, C, 12, { color: tint(C.primary, 0.45), letterSpacing: 5 })),
      picture(P + m, 360, pw, 300, 'company headquarters building exterior', { stroke: { color: C.primary, width: 4 } }),
      textBox(P + m, H - 100, pw, 60, div(bi(info, 'tagline')) + div(bi(info, 'website'), 'font-size: 11px; letter-spacing: 1px'), body(F, C, 13, { color: C.onDark, fontStyle: 'italic', lineHeight: 1.7 })),
      // Back (left half)
      textBox(m, 60, pw, 120, div(COPY.quote), heading(F, C, 24, { fontStyle: 'italic', fontWeight: 'normal', color: C.primary, textAlign: 'center', verticalAlign: 'middle' })),
      rect(P / 2 - 30, 196, 60, 2, C.primary),
      picture(m, 230, pw, 240, 'team portrait office group'),
      textBox(m, 496, pw, 44, div('Get in touch'), heading(F, C, 20, { textAlign: 'center' })),
      textBox(m, 540, pw, 150, paras(`<b>${bi(info, 'organization')}</b>`, ...biLines(info, 'address'), `${bi(info, 'phone')} · ${bi(info, 'email')}`, bi(info, 'website')), body(F, C, 12.5, { textAlign: 'center', lineHeight: 1.7, color: C.dark })),
      rect(m, H - 70, pw, 1, C.secondary),
      textBox(m, H - 60, pw, 30, div(`${bi(info, 'name')} · ${bi(info, 'title')}`), body(F, C, 11, { textAlign: 'center', color: C.secondaryText })),
    ];
    const inside = [
      // Left: who we are + timeline
      textBox(m, 48, pw, 52, div('Who we are'), heading(F, C, 28)),
      rect(m + 4, 104, 56, 3, C.primary),
      textBox(m, 122, pw, 150, div(COPY2.profileIntro), body(F, C, 12.5, { lineHeight: 1.55 })),
      textBox(m, 290, pw, 34, div('Our story'), label(F, C, 11, { letterSpacing: 3 })),
      rect(m + 15, 330, 2, 380, C.secondary),
      ...COPY2.milestones.flatMap(([year, text], i) => {
        const y = 330 + i * 76;
        return [
          shape('circle', m + 8, y, 16, 16, { fill: { type: 'solid', color: C.primary }, stroke: { color: '#ffffff', width: 2 } }),
          textBox(m + 40, y - 12, 90, 40, div(esc(year)), heading(F, C, 16, { color: C.primary, verticalAlign: 'middle' })),
          textBox(m + 130, y - 12, pw - 130, 60, div(esc(text)), body(F, C, 12, { verticalAlign: 'middle', lineHeight: 1.45 })),
        ];
      }),
      // Right: numbers + values
      rect(P, 0, P, H, C.pale),
      ...[['40', 'people'], ['300+', 'clients'], ['12', 'years']].flatMap(([n, t], i) => {
        const bw = (pw - 32) / 3;
        const x = P + m + i * (bw + 16);
        return [
          rect(x, 48, bw, 96, i === 1 ? C.primary : '#ffffff'),
          textBox(x, 52, bw, 60, div(n), heading(F, C, 30, { color: i === 1 ? C.onPrimary : C.primary, textAlign: 'center', verticalAlign: 'middle' })),
          textBox(x, 108, bw, 30, div(t), label(F, C, 9.5, { textAlign: 'center', color: i === 1 ? readableOn(C.primary) : C.secondary })),
        ];
      }),
      textBox(P + m, 172, pw, 48, div('What we stand for'), heading(F, C, 24)),
      rect(P + m + 4, 222, 56, 3, C.primary),
      ...COPY2.values.flatMap(([title, text], i) => {
        const y = 244 + i * 118;
        return [
          rect(P + m, y, 6, 90, C.secondary),
          textBox(P + m + 20, y - 4, pw - 20, 36, div(esc(title)), heading(F, C, 17, { verticalAlign: 'middle' })),
          textBox(P + m + 20, y + 30, pw - 20, 64, div(esc(text)), body(F, C, 12, { lineHeight: 1.5 })),
        ];
      }),
      picture(P + m, 610, pw, 130, 'office interior workspace plants'),
      rect(0, H - 40, W, 40, C.dark),
      textBox(m, H - 40, W - m * 2, 40, div(`${bi(info, 'organization')} · ${bi(info, 'website')} · ${bi(info, 'phone')}`), body(F, C, 11, { color: C.onDark, textAlign: 'center', verticalAlign: 'middle' })),
    ];
    return { guides: foldGuides([P]), pages: [{ name: 'Outside', objects: outside }, { name: 'Inside', objects: inside }] };
  },
};

/* ------------------------------------------------------------------ */
/* Menus                                                               */
/* ------------------------------------------------------------------ */

const menuRestaurant = {
  id: 'menu-restaurant', category: 'menu', name: 'Restaurant Menu', preset: 'letter', pageCount: 2,
  description: 'An elegant cover inside a fine double border and a two-column menu with dot-leader prices for starters, mains, desserts and drinks. Prices sit on a right-aligned tab stop — edit a line and they stay aligned.',
  build: ({ C, F, info }, { W, H }) => {
    const m = 60;
    const cw = W - m * 2;
    const cover = [
      shape('rectangle', 36, 36, W - 72, H - 72, { fill: { type: 'none' }, stroke: { color: C.primary, width: 3 } }),
      shape('rectangle', 48, 48, W - 96, H - 96, { fill: { type: 'none' }, stroke: { color: C.primary, width: 1 } }),
      picture(120, 90, W - 240, 190, 'restaurant interior candlelight table'),
      shape('diamond', W / 2 - 14, 300, 28, 28, { fill: { type: 'solid', color: C.primary } }),
      textBox(m, 350, cw, 120, div(bi(info, 'organization')), heading(F, C, 46, { textAlign: 'center', verticalAlign: 'middle', autofit: 'shrink' })),
      rect(W / 2 - 60, 478, 120, 1, C.dark),
      textBox(m, 490, cw, 50, div('Menu'), label(F, C, 16, { textAlign: 'center', letterSpacing: 8, color: C.primary })),
      textBox(m, 560, cw, 60, div(bi(info, 'tagline')), body(F, C, 14, { textAlign: 'center', fontStyle: 'italic', color: C.secondaryText })),
      shape('diamond', W / 2 - 14, 640, 28, 28, { fill: { type: 'solid', color: C.primary } }),
      textBox(m, H - 190, cw, 90, paras(addressInline(info), `${bi(info, 'phone')} · ${bi(info, 'website')}`), body(F, C, 11.5, { textAlign: 'center', lineHeight: 1.8, color: C.dark })),
    ];
    const colW = (cw - 40) / 2;
    const col = (x, y, title, items, h) => [
      textBox(x, y, colW, 40, div(esc(title)), heading(F, C, 22, { textAlign: 'center', verticalAlign: 'middle' })),
      rect(x + colW / 2 - 24, y + 42, 48, 1, C.primary),
      priceBox(x, y + 52, colW, h, priceLines(items, C), F, C, 12.5, { lineHeight: 1.45 }),
    ];
    const menu = [
      textBox(m, 44, cw, 44, div(bi(info, 'organization')), heading(F, C, 22, { textAlign: 'center', verticalAlign: 'middle', autofit: 'shrink' })),
      rect(m, 92, cw, 1, C.primary),
      ...col(m, 112, 'Starters', COPY2.restaurantMenu.Starters, 210),
      ...col(m, 400, 'Desserts', COPY2.restaurantMenu.Desserts, 210),
      textBox(m, 690, colW, 190, div('Set lunch menu', 'letter-spacing: 3px; font-size: 11px') + div('<br>') + paras('Two courses <b>24</b> · Three courses <b>29</b>', 'Tuesday to Friday, 12 – 2:30 PM. Choose any starter, main and dessert marked on today\u2019s board.'), body(F, C, 12, { color: C.dark, background: { type: 'solid', color: C.light }, paddingLeft: 18, paddingRight: 18, paddingTop: 16, paddingBottom: 16, lineHeight: 1.65 })),
      ...col(m + colW + 40, 112, 'Mains', COPY2.restaurantMenu.Mains, 260),
      ...col(m + colW + 40, 450, 'Drinks', COPY2.restaurantMenu.Drinks, 210),
      textBox(m + colW + 40, 740, colW, 140, div('Private dining', 'letter-spacing: 3px; font-size: 11px') + div('<br>') + div(`Our private room seats up to 16 guests. Ask a member of the team or call ${bi(info, 'phone')}.`), body(F, C, 12, { color: C.onSecondary, background: { type: 'solid', color: C.secondary }, paddingLeft: 18, paddingRight: 18, paddingTop: 16, paddingBottom: 16, lineHeight: 1.65 })),
      rect(m, H - 130, cw, 1, C.primary),
      textBox(m, H - 120, cw, 70, paras('Please let us know about any allergies or dietary requirements before ordering.', 'A discretionary 10% service charge is added to tables of six or more.'), body(F, C, 10.5, { textAlign: 'center', fontStyle: 'italic', color: C.secondaryText, lineHeight: 1.7 })),
    ];
    return { pages: [{ name: 'Cover', objects: cover }, { name: 'Menu', objects: menu }] };
  },
};

const menuCafe = {
  id: 'menu-cafe', category: 'menu', name: 'Café Menu', preset: 'letter', pageCount: 1,
  description: 'A friendly single-page café board: colour header with an “est.” badge, five sections in two columns with dot-leader prices, a photo strip and opening hours.',
  build: ({ C, F, info }, { W, H }) => {
    const m = 48;
    const cw = W - m * 2;
    const colW = (cw - 36) / 2;
    const sections = Object.entries(COPY2.cafeMenu);
    const sec = (x, y, title, items) => {
      const h = 34 + items.length * 22 + 10;
      return [[
        textBox(x, y, colW, 34, div(esc(title)), heading(F, C, 18, { color: C.primary, verticalAlign: 'middle' })),
        priceBox(x, y + 34, colW, h - 34, priceLines(items, C, { bold: false }), F, C, 12, { lineHeight: 1.6 }),
      ], h];
    };
    const left = [];
    const right = [];
    let ly = 236;
    let ry = 236;
    sections.forEach(([title, items], i) => {
      if (i % 2 === 0) { const [objs, h] = sec(m, ly, title, items); left.push(...objs); ly += h + 16; }
      else { const [objs, h] = sec(m + colW + 36, ry, title, items); right.push(...objs); ry += h + 16; }
    });
    const objects = [
      rect(0, 0, W, 190, C.secondary),
      textBox(m, 34, cw, 90, div(bi(info, 'organization')), heading(F, C, 44, { color: C.onSecondary, textAlign: 'center', verticalAlign: 'middle', autofit: 'shrink' })),
      textBox(m, 126, cw, 34, div('Café menu · all day'), label(F, C, 12, { color: readableOn(C.secondary), textAlign: 'center', letterSpacing: 4 })),
      shape('circle', W - m - 92, 150, 84, 84, { fill: { type: 'solid', color: C.strong }, stroke: { color: '#ffffff', width: 3 } }),
      textBox(W - m - 92, 150, 84, 84, div('est.', 'font-size: 10px; letter-spacing: 2px') + div('2012'), heading(F, C, 20, { color: C.onStrong, textAlign: 'center', verticalAlign: 'middle', lineHeight: 1.1 })),
      ...left, ...right,
      picture(m, 872, cw, 100, 'latte art coffee cups cafe counter'),
      rect(0, H - 64, W, 64, C.dark),
      textBox(m, H - 64, cw, 64, div(`Open daily 7 AM – 5 PM · ${addressInline(info)} · ${bi(info, 'phone')}`), body(F, C, 11.5, { color: C.onDark, textAlign: 'center', verticalAlign: 'middle' })),
    ];
    return { pages: [{ name: 'Menu', objects }] };
  },
};

const menuTakeaway = {
  id: 'menu-takeaway', category: 'menu', name: 'Takeaway Menu', preset: 'letter', pageCount: 1,
  description: 'Bold takeaway leaflet: strong colour header with a call-to-order box, six category cards in a grid with right-aligned prices, and a delivery banner.',
  build: ({ C, F, info }, { W, H }) => {
    const m = 40;
    const cw = W - m * 2;
    const cardW = (cw - 24) / 2;
    const cardH = 236;
    const cards = COPY2.takeawayMenu.flatMap(([title, items], i) => {
      const x = m + (i % 2) * (cardW + 24);
      const y = 190 + Math.floor(i / 2) * (cardH + 18);
      return [
        rect(x, y, cardW, cardH, '#ffffff', { cornerRadius: 10 }),
        rect(x, y, cardW, 40, i % 2 ? C.secondary : C.primary, { cornerRadius: 10 }),
        rect(x, y + 20, cardW, 20, i % 2 ? C.secondary : C.primary),
        textBox(x, y, cardW, 40, div(esc(title)), heading(F, C, 17, { color: i % 2 ? C.onSecondary : C.onPrimary, verticalAlign: 'middle', paddingLeft: 16, textTransform: 'uppercase', letterSpacing: 2 })),
        priceBox(x + 8, y + 46, cardW - 16, cardH - 52, priceLines(items, C, { bold: false }), F, C, 12, { lineHeight: 1.65, paddingLeft: 10, paddingRight: 10 }),
      ];
    });
    const objects = [
      rect(0, 0, W, 150, C.strong),
      textBox(m, 26, cw - 250, 70, div(bi(info, 'organization')), heading(F, C, 38, { color: C.onStrong, verticalAlign: 'middle', autofit: 'shrink' })),
      textBox(m, 96, cw - 250, 34, div(bi(info, 'tagline')), body(F, C, 13, { color: readableOn(C.strong), fontStyle: 'italic' })),
      rect(W - m - 230, 24, 230, 102, C.dark, { cornerRadius: 10 }),
      textBox(W - m - 230, 30, 230, 30, div('Call to order'), label(F, C, 10, { color: tint(C.primary, 0.45), textAlign: 'center', letterSpacing: 3 })),
      textBox(W - m - 230, 58, 230, 60, div(bi(info, 'phone')), heading(F, C, 26, { color: C.onDark, textAlign: 'center', verticalAlign: 'middle', autofit: 'shrink' })),
      ...cards,
      rect(0, H - 96, W, 96, C.primary),
      textBox(m, H - 90, cw, 44, div('Free delivery on orders over 25 · Open daily 11 AM – 11 PM'), heading(F, C, 18, { color: C.onPrimary, textAlign: 'center', verticalAlign: 'middle' })),
      textBox(m, H - 48, cw, 40, div(`${addressInline(info)} · ${bi(info, 'website')}`), body(F, C, 11.5, { color: readableOn(C.primary), textAlign: 'center', verticalAlign: 'middle' })),
    ];
    return { pages: [{ name: 'Menu', background: { type: 'solid', color: C.pale }, objects }] };
  },
};

/* ------------------------------------------------------------------ */
/* Certificates (Letter landscape)                                     */
/* ------------------------------------------------------------------ */

const signatureLines = (F, C, y, W, entries) => entries.flatMap(([labelText], i) => {
  const lw = 240;
  const gap = (W - entries.length * lw) / (entries.length + 1);
  const x = gap + i * (lw + gap);
  return [
    rect(x, y, lw, 1, C.dark),
    textBox(x, y + 6, lw, 40, div(esc(labelText)), label(F, C, 9, { textAlign: 'center', letterSpacing: 2 })),
  ];
});

const seal = (x, y, size, C, F, text) => [
  shape('star-16', x, y, size, size, { fill: { type: 'solid', color: C.strong } }),
  shape('circle', x + size * 0.17, y + size * 0.17, size * 0.66, size * 0.66, { fill: { type: 'none' }, stroke: { color: readableOn(C.strong), width: 1.5 } }),
  textBox(x + size * 0.17, y + size * 0.17, size * 0.66, size * 0.66, text, heading(F, C, Math.round(size * 0.085), { color: C.onStrong, textAlign: 'center', verticalAlign: 'middle', letterSpacing: 0.5, textTransform: 'uppercase', lineHeight: 1.3, paddingLeft: 0, paddingRight: 0 })),
];

const certificateAchievement = {
  id: 'certificate-achievement', category: 'certificate', name: 'Certificate of Achievement', preset: 'letter', landscape: true, pageCount: 1,
  description: 'Classic landscape certificate with a double rule border, corner ornaments, your logo, a scheme-coloured recipient line, an embossed seal and signature lines.',
  build: ({ C, F, info }, { W, H }) => {
    const objects = [
      shape('rectangle', 30, 30, W - 60, H - 60, { fill: { type: 'none' }, stroke: { color: C.dark, width: 4 } }),
      shape('rectangle', 44, 44, W - 88, H - 88, { fill: { type: 'none' }, stroke: { color: C.primary, width: 1.5 } }),
      ...[[52, 52], [W - 76, 52], [52, H - 76], [W - 76, H - 76]].map(([x, y]) => shape('diamond', x, y, 24, 24, { fill: { type: 'solid', color: C.primary } })),
      ...logoBlock(info, W / 2 - 36, 72, 72, C, F),
      textBox(120, 154, W - 240, 80, div('Certificate'), heading(F, C, 54, { textAlign: 'center', verticalAlign: 'middle' })),
      textBox(120, 232, W - 240, 34, div('of Achievement'), label(F, C, 14, { textAlign: 'center', letterSpacing: 8, color: C.primary })),
      textBox(120, 292, W - 240, 30, div('This certificate is proudly presented to'), body(F, C, 13, { textAlign: 'center', fontStyle: 'italic', color: C.secondaryText })),
      textBox(W / 2 - 320, 328, 640, 76, div('Recipient Name'), heading(F, C, 42, { textAlign: 'center', verticalAlign: 'middle', color: C.primary, autofit: 'shrink' })),
      rect(W / 2 - 260, 408, 520, 1, C.dark),
      textBox(160, 428, W - 320, 90, div(COPY2.achievement), body(F, C, 14, { textAlign: 'center', lineHeight: 1.6, color: C.dark })),
      textBox(160, 520, W - 320, 30, div(`Awarded by ${bi(info, 'organization')} on this day, 1 June 2025`), body(F, C, 12, { textAlign: 'center', color: C.secondaryText })),
      ...seal(W / 2 - 60, 570, 120, C, F, div('Award of') + div('excellence')),
      ...signatureLines(F, C, 690, W, [['Signature · Name, Title'], ['Date']]),
      textBox(120, H - 66, W - 240, 24, div(`${bi(info, 'organization')} · ${bi(info, 'website')}`), body(F, C, 10, { textAlign: 'center', color: C.secondaryText, letterSpacing: 1 })),
    ];
    return { pages: [{ name: 'Certificate', objects }] };
  },
};

const certificateAppreciation = {
  id: 'certificate-appreciation', category: 'certificate', name: 'Certificate of Appreciation', preset: 'letter', landscape: true, pageCount: 1,
  description: 'Modern certificate with a colour side band carrying your logo and rotated organisation name, a ribbon badge, a highlighted recipient line and signature blocks.',
  build: ({ C, F, info }, { W, H }) => {
    const band = 200;
    const x = band + 60;
    const cw = W - x - 60;
    const objects = [
      rect(0, 0, band, H, C.primary),
      rect(band, 0, 6, H, C.strong),
      ...logoBlock(info, band / 2 - 40, 60, 80, C, F, { onDark: true }),
      textBox(band / 2 - 30, 170, 60, H - 250, div(bi(info, 'organization')), heading(F, C, 26, { color: C.onPrimary, textDirection: 'vertical', verticalAlign: 'middle', textAlign: 'center', autofit: 'shrink', letterSpacing: 2 })),
      ...seal(W - 190, 44, 130, C, F, div('Thank') + div('you')),
      textBox(x, 90, cw - 140, 40, div('Certificate of'), label(F, C, 14, { letterSpacing: 6, color: C.secondaryText })),
      textBox(x, 128, cw - 140, 90, div('Appreciation'), heading(F, C, 58, { verticalAlign: 'middle', autofit: 'shrink' })),
      textBox(x, 240, cw, 30, div('is presented with gratitude to'), body(F, C, 13.5, { fontStyle: 'italic', color: C.secondaryText })),
      rect(x, 282, cw, 74, C.light),
      textBox(x, 282, cw, 74, div('Recipient Name'), heading(F, C, 38, { color: C.primary, verticalAlign: 'middle', paddingLeft: 20, autofit: 'shrink' })),
      textBox(x, 380, cw, 110, div(COPY2.appreciation), body(F, C, 14, { lineHeight: 1.65, color: C.dark })),
      textBox(x, 500, cw, 30, div(`Presented by ${bi(info, 'organization')} · 1 June 2025`), body(F, C, 12, { color: C.secondaryText })),
      ...[[x, 'Signature · Name, Title'], [x + 330, 'Signature · Name, Title']].flatMap(([sx, t]) => [rect(sx, 640, 260, 1, C.dark), textBox(sx, 646, 260, 30, div(t), label(F, C, 9, { letterSpacing: 2 }))]),
      textBox(x, H - 70, cw, 30, div(`${addressInline(info)} · ${bi(info, 'phone')} · ${bi(info, 'website')}`), body(F, C, 10, { color: C.secondaryText })),
    ];
    return { pages: [{ name: 'Certificate', objects }] };
  },
};

const certificateGift = {
  id: 'certificate-gift', category: 'certificate', name: 'Gift Certificate', preset: 'letter', landscape: true, pageCount: 1,
  description: 'Gift voucher on a soft scheme background: a dashed cut-out frame, recipient / from / issued lines with tab leaders, a bold value panel with the certificate number and expiry, and terms in small print.',
  build: ({ C, F, info }, { W, H }) => {
    const m = 60;
    const panelW = 320;
    const left = W - m * 2 - panelW - 40;
    const objects = [
      shape('rectangle', m - 20, m - 20, W - (m - 20) * 2, H - (m - 20) * 2, { fill: { type: 'solid', color: '#ffffff' }, stroke: { color: C.secondary, width: 1.5, dash: 'dashed' } }),
      ...logoBlock(info, m, m, 64, C, F),
      textBox(m + 80, m, left - 80, 64, div(bi(info, 'organization')) + div(bi(info, 'tagline'), `font-size: 12px; font-weight: normal; font-style: italic; color: ${C.secondaryText}`), heading(F, C, 22, { verticalAlign: 'middle', autofit: 'shrink' })),
      textBox(m, 170, left, 40, div('Gift'), label(F, C, 16, { letterSpacing: 8, color: C.primary })),
      textBox(m, 206, left, 90, div('Certificate'), heading(F, C, 60, { verticalAlign: 'middle', autofit: 'shrink' })),
      textBox(m, 316, left, 190, ['To', 'From', 'Message', 'Issued on'].map((k) => div(`<b>${k}</b>\t\t`)).join(''), body(F, C, 14, { lineHeight: 2.6, tabStops: [{ pos: 110, align: 'left', leader: 'none' }, { pos: left - 14, align: 'right', leader: 'line' }] })),
      textBox(m, 530, left, 30, div('Terms & conditions'), label(F, C, 9.5, { letterSpacing: 3 })),
      textBox(m, 556, left, 90, div(COPY2.giftTerms), body(F, C, 10, { lineHeight: 1.6, color: C.secondaryText })),
      textBox(m, H - 100, left, 30, div(`${addressInline(info)} · ${bi(info, 'phone')} · ${bi(info, 'website')}`), body(F, C, 10.5, { color: C.dark })),
      rect(W - m - panelW, m, panelW, H - m * 2, C.primary, { cornerRadius: 12 }),
      textBox(W - m - panelW, m + 40, panelW, 34, div('Gift value'), label(F, C, 12, { color: readableOn(C.primary), textAlign: 'center', letterSpacing: 5 })),
      textBox(W - m - panelW, m + 80, panelW, 150, div('$50'), heading(F, C, 96, { color: C.onPrimary, textAlign: 'center', verticalAlign: 'middle', autofit: 'shrink' })),
      rect(W - m - panelW + 60, m + 244, panelW - 120, 1, readableOn(C.primary)),
      textBox(W - m - panelW, m + 262, panelW, 130, div('Certificate no.', 'font-size: 10px; letter-spacing: 3px') + div('000 123') + div('<br>') + div('Valid until', 'font-size: 10px; letter-spacing: 3px') + div('1 June 2026'), heading(F, C, 20, { color: C.onPrimary, textAlign: 'center', lineHeight: 1.35 })),
      ...seal(W - m - panelW / 2 - 50, H - m - 150, 100, C, F, div('Gift')),
    ];
    return { pages: [{ name: 'Gift certificate', background: { type: 'solid', color: C.pale }, objects }] };
  },
};

export const TEMPLATES = [
  flyerEvent, flyerSale, flyerNotice,
  newsletterClassic, newsletterModern, newsletterBulletin, newsletterDigest,
  brochureTrifoldServices, brochureTrifoldProduct, brochureBifoldProfile,
  menuRestaurant, menuCafe, menuTakeaway,
  certificateAchievement, certificateAppreciation, certificateGift,
  cardMinimal, cardBold, cardElegant, postcardAnnounce,
];

/* ------------------------------------------------------------------ */
/* Build                                                               */
/* ------------------------------------------------------------------ */

/**
 * Build a full project payload (as accepted by SET_PROJECT) from a template.
 */
export const buildTemplateProject = (template, { schemeId = 'office', fontSchemeId = 'modern', info = {} } = {}) => {
  const scheme = SCHEME_PRESETS[schemeId] || SCHEME_PRESETS.office;
  const C = palette(scheme);
  const F = FONT_SCHEMES[fontSchemeId] || FONT_SCHEMES.modern;
  const preset = PAGE_PRESETS[template.preset] || PAGE_PRESETS.letter;
  // Brochures and certificates are landscape: swap the preset's sides
  const pageW = template.landscape ? Math.max(preset.width, preset.height) : preset.width;
  const pageH = template.landscape ? Math.min(preset.width, preset.height) : preset.height;
  const spec = template.build({ C, F, info: info || {} }, { W: pageW, H: pageH });
  const pages = spec.pages.map((pg, i) => ({
    id: uid(),
    name: pg.name || `Page ${i + 1}`,
    width: pageW,
    height: pageH,
    background: pg.background || { type: 'solid', color: '#ffffff' },
    masterId: pg.masterId || 'default',
    objects: (pg.objects || []).map((o, idx) => ({ ...o, layer: idx })),
  }));
  const masters = { default: { id: 'default', name: 'Master A', objects: (spec.master || []).map((o, idx) => ({ ...o, layer: idx })) } };
  const orgName = ((info && info.organization) || '').trim();
  return {
    id: null,
    name: orgName ? `${orgName} ${template.name}` : template.name,
    pages,
    masters,
    scheme_id: schemeId,
    font_scheme: { id: FONT_SCHEMES[fontSchemeId] ? fontSchemeId : 'modern', name: F.name, heading: F.heading, body: F.body },
    canvas_unit: 'in',
    grid_visible: false,
    grid_spacing: 20,
    // e.g. fold lines of a brochure
    guides: (spec.guides || []).map((g) => ({ id: uid(), ...g })),
  };
};

/* ------------------------------------------------------------------ */
/* Custom templates (saved publications) + scheme recolouring          */
/* ------------------------------------------------------------------ */

const HEX_RE = /#[0-9a-f]{6}\b/gi;
const norm = (hex) => String(hex || '').toLowerCase();

/**
 * Colour map from one scheme to another: the eight scheme colours, their Office-style tints/shades and the derived
 * palette values templates use (light / soft / pale / primaryDark). Pure black and white are never remapped.
 */
export const schemeColorMap = (fromId, toId) => {
  const from = SCHEME_PRESETS[fromId], to = SCHEME_PRESETS[toId];
  const map = new Map();
  if (!from || !to || fromId === toId) return map;
  const add = (a, b) => { const ka = norm(a); if (!ka || ka === '#ffffff' || ka === '#000000' || map.has(ka)) return; map.set(ka, norm(b)); };
  from.forEach((c, i) => {
    add(c.hex, to[i].hex);
    const va = schemeVariants(c.hex), vb = schemeVariants(to[i].hex);
    va.forEach((v, k) => add(v, vb[k]));
  });
  const pa = palette(from), pb = palette(to);
  ['light', 'soft', 'pale', 'primaryDark', 'secondaryText'].forEach((k) => add(pa[k], pb[k]));
  return map;
};

const recolorValue = (v, map) => {
  if (typeof v === 'string') {
    if (/^#[0-9a-f]{6}$/i.test(v)) return map.get(norm(v)) || v;
    if (v.includes('#')) return v.replace(HEX_RE, (m) => map.get(norm(m)) || m); // inline styles inside html
    return v;
  }
  if (Array.isArray(v)) return v.map((x) => recolorValue(x, map));
  if (v && typeof v === 'object') {
    const out = {};
    for (const k of Object.keys(v)) out[k] = k === 'src' || k === 'url' || k === 'id' ? v[k] : recolorValue(v[k], map); // never touch picture sources / ids
    return out;
  }
  return v;
};

/** Publisher-style: a saved publication adopts another colour scheme by mapping its scheme colours (fonts untouched). */
export const recolorProject = (project, fromSchemeId, toSchemeId) => {
  const map = schemeColorMap(fromSchemeId, toSchemeId);
  if (!map.size) return project;
  return { ...project, pages: recolorValue(project.pages, map), masters: recolorValue(project.masters, map), scheme_id: toSchemeId };
};

/** Project payload (SET_PROJECT) from a custom template, optionally recoloured to another scheme. */
export const customTemplateProject = (tpl, { schemeId } = {}) => {
  const src = tpl.project || {};
  let project = {
    id: null,
    name: tpl.name,
    pages: JSON.parse(JSON.stringify(src.pages || [])),
    masters: JSON.parse(JSON.stringify(src.masters || {})),
    guides: JSON.parse(JSON.stringify(src.guides || [])),
    scheme_id: tpl.scheme_id || 'office',
    custom_schemes: Array.isArray(src.custom_schemes) ? JSON.parse(JSON.stringify(src.custom_schemes)) : [],
    font_scheme: src.font_scheme || undefined,
    canvas_unit: src.canvas_unit || 'in',
    grid_visible: false,
    grid_spacing: 20,
  };
  // the template's own colour schemes must be resolvable for the recolouring below
  project.custom_schemes.forEach((sc) => ensureSchemeRegistered(sc));
  if (!project.masters.default) project.masters.default = { id: 'default', name: 'Master A', objects: [] };
  if (schemeId && schemeId !== project.scheme_id) project = recolorProject(project, project.scheme_id, schemeId);
  return project;
};

/** The payload stored when a publication is saved as a template. */
export const customTemplatePayload = (state, { name, description = '' }) => ({
  name,
  description,
  scheme_id: state.schemeId || 'office',
  project: {
    pages: state.pages.map((p) => ({ id: p.id, name: p.name, width: p.width, height: p.height, objects: p.objects, background: p.background, masterId: p.masterId || 'default' })),
    masters: state.masters,
    guides: state.guides,
    custom_schemes: state.customSchemes || [],
    font_scheme: state.fontScheme || undefined,
    canvas_unit: state.canvasUnit,
  },
});

/**
 * Replace picture placeholders with stock photos. `search(query)` must resolve to an
 * array of { url, photographer, source, id, description }. Failures leave placeholders intact.
 */
export const fillStockPhotos = async (project, search) => {
  const targets = [];
  for (const page of project.pages) for (const o of page.objects) if (o.type === 'image' && o.properties?.placeholder && o.properties.photoQuery) targets.push(o);
  if (!targets.length) return project;
  const queries = [...new Set(targets.map((t) => t.properties.photoQuery))];
  const results = new Map();
  await Promise.all(queries.map(async (q) => {
    try { results.set(q, (await search(q)) || []); } catch { results.set(q, []); }
  }));
  const used = new Set();
  for (const t of targets) {
    const list = (results.get(t.properties.photoQuery) || []).filter((r) => r.url && !used.has(r.id));
    // prefer an orientation that matches the frame
    const landscape = t.width >= t.height;
    const pick = list.find((r) => (r.width >= r.height) === landscape) || list[0];
    if (!pick) continue;
    used.add(pick.id);
    t.properties = { ...t.properties, src: pick.url, placeholder: false, altText: pick.description || t.properties.altText, attribution: { photographer: pick.photographer, source: pick.source, id: pick.id } };
  }
  return project;
};

/** Shared object builders, exposed for other content libraries (Building Blocks) so they stay scheme-aware. */
export const TEMPLATE_KIT = { palette, textBox, shape, rect, picture, heading, body, label, div, paras, bi, biLines, addressInline };
